import {
  type AnyRecord,
  type RecordAction,
  type RecordApiKind,
  type ReviewQueueItem,
  type ReviewQueueKind,
  reviewActionLabel,
} from '@fruuit/shared'
import {
  Button,
  buttonClasses,
  Callout,
  Drawer,
  SkeletonText,
  StatusBadge,
  Tabs,
  UiLink,
} from '@fruuit/ui'
import { ArrowUpRight } from 'lucide-react'
import { useState } from 'react'
import { errorMessage } from '../../api/client'
import { useRecord } from '../records/api'
import { formatDate, formatDateTime } from '../records/format'
import { HistoryPanel } from '../records/HistoryPanel'
import { RecordSummary } from '../records/RecordSummary'
import { blockedMessage, INDEPENDENCE_MESSAGE, type ReviewDialogAction } from './copy'
import { ReviewActionDialog } from './ReviewActionDialog'
import { WorkflowStrip } from './WorkflowStrip'

const DIALOG_ACTIONS: readonly ReviewDialogAction[] = [
  'submit',
  'approve',
  'publish',
  'release',
  'return',
  'retract',
]

function isDialogAction(action: RecordAction): action is ReviewDialogAction {
  return (DIALOG_ACTIONS as readonly string[]).includes(action)
}

/** Where to open the full record outside the queue. */
export function recordHref(
  item: Pick<ReviewQueueItem, 'kind' | 'id' | 'engagement_id' | 'meeting_id'>,
) {
  if (item.kind === 'assessments') return `/workspace/assessments/${item.id}`
  if (item.kind === 'minutes' && item.meeting_id) return `/workspace/meetings/${item.meeting_id}`
  if (item.engagement_id) return `/workspace/cases/${item.engagement_id}`
  return null
}

export interface ReviewDrawerProps {
  item: ReviewQueueItem | null
  onClose: () => void
}

/** S14 detail drawer: the record, its contributors and history, and the review actions. */
export function ReviewDrawer({ item, onClose }: ReviewDrawerProps) {
  return (
    <Drawer
      open={item !== null}
      onClose={onClose}
      title={item ? `${item.label}: ${item.title}` : 'Record'}
      className="w-[min(46rem,100vw)]!"
    >
      {item && item.kind !== 'minutes' ? (
        <ReviewDrawerBody key={`${item.kind}:${item.id}`} item={item} kind={item.kind} />
      ) : null}
    </Drawer>
  )
}

function ReviewDrawerBody({
  item,
  kind,
}: {
  item: ReviewQueueItem
  kind: Exclude<RecordApiKind, 'issuers' | 'securities' | 'sources' | 'incidents'>
}) {
  const detail = useRecord(kind, item.id)
  const [action, setAction] = useState<ReviewDialogAction | null>(null)
  const [tab, setTab] = useState('record')

  if (detail.isError) {
    return (
      <div className="p-5">
        <Callout tone="danger" role="alert">
          {errorMessage(detail.error)}
        </Callout>
      </div>
    )
  }
  if (!detail.data) {
    return (
      <div className="p-5">
        <SkeletonText lines={8} />
      </div>
    )
  }
  const record = detail.data
  const reviewed = 'state' in record
  const actions = record.actions.filter(isDialogAction)
  const blocked =
    blockedMessage(record.blocked_reason) ??
    (item.caller_is_contributor && ['in_review', 'approved'].includes(item.state)
      ? INDEPENDENCE_MESSAGE
      : null)
  const href = recordHref(item)
  const isRelease = item.kind === 'engagements' || item.kind === 'objectives'

  return (
    <div className="flex flex-col gap-5 px-5 py-5">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge
          status={item.state === 'pending_release' ? 'not released' : item.state}
          label={item.state === 'pending_release' ? 'Unreleased changes' : undefined}
        />
        {reviewed && !isRelease ? (
          <span className="text-sm text-sage">Version {record.version}</span>
        ) : null}
        <span className="text-sm text-sage">
          · {item.client_org_name ?? 'Shared with all subscribers'}
        </span>
        {href ? (
          <UiLink
            href={href}
            className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-teal hover:underline"
          >
            Open full record
            <ArrowUpRight aria-hidden="true" className="size-4" />
          </UiLink>
        ) : null}
      </div>
      {!isRelease ? <WorkflowStrip state={item.state} /> : null}
      {blocked ? (
        <Callout tone="warning" title="Independent review required" role="status">
          {blocked}
        </Callout>
      ) : null}
      {record.state === 'returned' && record.return_reason ? (
        <Callout tone="note" title="Returned for changes">
          {record.return_reason}
        </Callout>
      ) : null}
      {record.submission_note ? (
        <Callout tone="info" title="Note from the author">
          {record.submission_note}
        </Callout>
      ) : null}

      <Tabs
        label="Record sections"
        value={tab}
        onValueChange={setTab}
        items={[
          {
            id: 'record',
            label: 'Record',
            content: <RecordSummary record={record as AnyRecord} people={record.people} />,
          },
          {
            id: 'contributors',
            label: 'Contributors',
            count: record.contributors.length,
            content: (
              <div className="flex flex-col gap-3">
                {record.contributors.length === 0 ? (
                  <p className="text-sm text-sage">No edits recorded.</p>
                ) : (
                  <ul className="divide-y divide-line rounded-md border border-line">
                    {record.contributors.map((person) => (
                      <li
                        key={person.user_id}
                        className="flex flex-wrap justify-between gap-2 px-3 py-2.5 text-sm"
                      >
                        <span className="font-medium text-ink">{person.display_name}</span>
                        <span className="text-sage">
                          {person.edit_count} {person.edit_count === 1 ? 'edit' : 'edits'} ·{' '}
                          {formatDate(person.first_edited_at)} – {formatDate(person.last_edited_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs leading-relaxed text-sage">
                  Contributors cannot approve, publish, return or retract this record (D-07).
                  {record.submitted_at
                    ? ` Submitted ${formatDateTime(record.submitted_at)}${record.submitted_by ? ` by ${record.people[record.submitted_by] ?? 'a colleague'}` : ''}.`
                    : ''}
                </p>
              </div>
            ),
          },
          {
            id: 'history',
            label: 'History',
            content: <HistoryPanel kind={kind} id={record.id} currentId={record.id} />,
          },
        ]}
      />

      <div className="sticky bottom-0 -mx-5 -mb-5 flex flex-wrap gap-2 border-t border-line bg-surface px-5 py-4">
        {actions.length === 0 ? (
          <p className="text-sm text-sage">
            {blocked ?? 'No review action is available to you for this record right now.'}
          </p>
        ) : (
          actions.map((next) => (
            <Button
              key={next}
              variant={next === 'return' || next === 'retract' ? 'secondary' : 'primary'}
              onClick={() => setAction(next)}
            >
              {reviewActionLabel(item.kind as ReviewQueueKind, next)}
            </Button>
          ))
        )}
        {href && actions.length === 0 ? (
          <UiLink
            href={href}
            className={buttonClasses({ variant: 'secondary', className: 'ml-auto' })}
          >
            Open full record
          </UiLink>
        ) : null}
      </div>

      <ReviewActionDialog
        action={action}
        target={{
          kind,
          id: record.id,
          revision: record.revision,
          title: `${item.label}: ${item.title}`,
        }}
        onClose={() => setAction(null)}
      />
    </div>
  )
}
